<template>
  <div
    class="race-information-section"
    :class="{ 'race-information-section--is-open': showInformation }"
  >
    <div class="race-information-section__content">
      <div v-if="racePointRadioData.length > 0" class="race-information-section__points-list">
        <div
          v-for="(item, index) in racePointRadioData"
          :key="`${item.raceRadioData.audio_clip} + ${index}`"
          class="race-information-section__points-item"
        >
          <race-point-radio-card-parts :pointsCardData="item" @radioPlay="playRadioAudio" />
        </div>
      </div>
      <div v-if="racePointVideoData.length > 0" class="race-information-section__points-list">
        <div
          v-for="(racePointVideo, index) in racePointVideoData"
          :key="`racePointsVideoCard${index}`"
          class="race-information-section__points-item"
        >
          <race-point-video-card-parts :racePointVideoData="racePointVideo" />
        </div>
      </div>

      <div v-if="rankingsData.length > 0" class="race-information-section__ranking">
        <h3 class="race-information-section__ranking-title">
          {{ $tc('RaceListPage.Information.RaceRank') }}
        </h3>
        <p class="race-information-section__ranking-attention">
          {{ $tc('RaceListPage.Information.RaceRankAttention') }}
        </p>

        <div class="race-information-section__ranking-list">
          <ranking-card-parts
            v-for="(ranking, index) in rankingsData"
            :key="`RankingCard${index}`"
            :rankingCardData="ranking"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from '@vue/composition-api'
import RacePointRadioCardParts from '@/components/RaceListPage/RaceListPane/parts/RacePointRadioCardParts.vue'
import RacePointVideoCardParts from '@/components/RaceListPage/RaceListPane/parts/RacePointVideoCardParts.vue'
import RankingCardParts from '@/components/RaceListPage/RaceListPane/parts/RankingCardParts.vue'
import StoreUtil from '@/store/StoreUtil'
import RadioDataDocument from '@/store/stores/collectionModule/documents/Radio/RadioDataDocument'

/**
 * レース一覧画面: Race Information セクション
 */
export default defineComponent({
  name: 'RaceInformationSection',
  components: {
    RacePointVideoCardParts,
    RacePointRadioCardParts,
    RankingCardParts,
  },
  props: {
    /**
     * Information 部開閉フラグ
     */
    showInformation: {
      type: Boolean,
      default: true,
    },
  },
  setup: () => {
    const store = StoreUtil.useStore('TopPageStore')

    /**
     * Race Rankのデータを取得する
     */
    const rankingsData = computed(() => store.getRankingCardData.value)
    /**
     * Race Point Videoのデータを取得する
     */
    const racePointVideoData = computed(() => store.racePointVideoData.value)
    /**
     * Race Point Radioのデータを取得する
     */
    const racePointRadioData = computed(() => store.racePointRadioData.value)

    return {
      rankingsData,
      racePointVideoData,
      racePointRadioData,
    }
  },
  methods: {
    /**
     * 無線交信データを再生する。
     * @param radioData 無線交信データ
     */
    playRadioAudio(radioData: RadioDataDocument) {
      this.$emit('radioPlay', radioData)
    },
  },
})
</script>

<style lang="scss" scoped>
.race-information-section {
  width: 100%;
  max-width: 460px;
  padding: 0 min(#{convertPXtoVW(30)}, 30px) 0;
  margin: 0 auto;
  color: $black;
  overflow-y: scroll;

  &__content {
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: center;
    gap: 24px;
    width: 100%;
  }

  &__points-list {
    display: flex;
    flex-flow: column nowrap;
    align-items: stretch;
    justify-content: center;
    gap: 12px;
    width: 100%;
  }

  &__ranking {
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    gap: 8px;
    color: $white;

    &-title {
      font-style: normal;
      font-weight: 600;
      font-size: min(#{convertPXtoVW(14)}, 14px);
      line-height: 1.57;
      text-align: center;
      letter-spacing: 0.04em;
    }

    &-attention {
      font-weight: 500;
      font-size: min(#{convertPXtoVW(10)}, 10px);
      line-height: 1.4;
      letter-spacing: 0.02em;
      font-feature-settings: 'palt' on;
      text-align: center;
    }

    &-list {
      display: flex;
      flex-flow: column nowrap;
      width: 100%;
      gap: 8px;
    }
  }

  &--is-open {
    padding: 44px min(#{convertPXtoVW(30)}, 30px) 280px;
  }
}
</style>
